import { ApiError, apiFetch } from './api-fetch.ts'

export type RegistryKind =
  | 'harnesses'
  | 'cli-profiles'
  | 'failover-configs'
  | 'agent-roles'
  | 'agent-definitions'
  | 'mcp-definitions'
  | 'skill-definitions'

export const registryKinds: RegistryKind[] = [
  'harnesses',
  'cli-profiles',
  'failover-configs',
  'agent-roles',
  'agent-definitions',
  'mcp-definitions',
  'skill-definitions',
]

export function isRegistryKind(value: string): value is RegistryKind {
  return (registryKinds as string[]).includes(value)
}

// The API's row shapes differ per kind (Harness, CliProfile, ...), but the
// admin panel here is deliberately JSON-first: it never needs to know a
// row's exact fields beyond the few used for list-item display and the
// id/timestamps stripped before editing.
export interface RegistryRow {
  id: string
  rootId: string
  name?: string | null
  title?: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

export async function listRegistry(
  kind: RegistryKind,
  rootId?: string,
): Promise<RegistryRow[]> {
  const query = rootId ? `?rootId=${encodeURIComponent(rootId)}` : ''
  const { items } = await apiFetch<{ items: RegistryRow[] }>(
    `/registry/${kind}${query}`,
  )
  return items
}

export function createRegistry(
  kind: RegistryKind,
  body: Record<string, unknown>,
): Promise<RegistryRow> {
  return apiFetch<RegistryRow>(`/registry/${kind}`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function patchRegistry(
  kind: RegistryKind,
  id: string,
  body: Record<string, unknown>,
): Promise<RegistryRow> {
  return apiFetch<RegistryRow>(`/registry/${kind}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function deleteRegistry(
  kind: RegistryKind,
  id: string,
): Promise<void> {
  await apiFetch<null>(`/registry/${kind}/${id}`, { method: 'DELETE' })
}

export function seedRegistry(rootId: string): Promise<unknown> {
  return apiFetch<unknown>('/registry/seed', {
    method: 'POST',
    body: JSON.stringify({ rootId }),
  })
}

export function issueEnrollmentToken(
  harnessId: string,
): Promise<{ enrollmentToken: string }> {
  return apiFetch<{ enrollmentToken: string }>(
    `/registry/harnesses/${harnessId}/enrollment-token`,
    { method: 'POST' },
  )
}

// `ApiError` moved to `api-fetch.ts` with the shared fetch; re-exported here
// so existing importers of this module keep working.
export { ApiError }
